import React, { useState } from 'react';
import TaskCard from '../../TaskCard';
import Cards from '../Cards';
import './NotificationsBar.css';
import {
  acceptFeedbackRequest,
  declineFeedbackRequest,
  acceptShare,
  rejectShare,
  getMarkingMethodology,
  getCommentBank,
  getAssignmentById,
  getSuccessCriteria,
  getShortAnswer,
  neverShowSharedResourcePopup,
} from '../../../service';
import ActionPopup from '../../../components2/ActionPopup';
import CommentBankDialog from '../../Shared/Dialogs/commentBank';
import PreviewDialog from '../../Shared/Dialogs/preview/previewCard';
import TaskPreviewDialog from '../../Shared/Dialogs/taskPreview';
import SuccessCriteriaDialog from '../../Shared/Dialogs/successCriteria';
import ShortAnswerDialog from '../../Shared/Dialogs/shortAnswer';
import previewIcon from '../../../static/img/preview.svg';
import acceptIcon from '../../../static/img/accept-icon.svg';
import rejectIcon from '../../../static/icons/red-cross.svg';
import {
  NavbarDiv,
  Frame1409,
  MaskGroup,
  Frame15,
  Frame16,
  NotificationHead,
  CloseImage,
  HeaderLeft,
  EmptyBox,
  CloseNotification,
  NotificationBody,
  HeadingImage,
  Heading,
  ActionButtons,
  PreviewButton,
  AcceptButton,
  RejectButton,
  SharedNotificationContainer,
  SharedNotificationTitle,
  SharedNotificationSender,
  SharedNotificationList,
  SharedNotificationItem,
  SharedNotificationItemTitle,
} from './style';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import NotificationSwitch from './NotificationSwitch';
import Toast from '../../Toast';
import { toast } from 'react-toastify';
import notificationbing from '../../../static/img/notificationbing.svg';
import { useProfile } from '../../state/hooks';
import Loader from '../../Loader';
import SmallLoader from '../../Loader/SmallLoader';

function NotificationsBar(props) {
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewedNotification, setPreviewedNotification] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const queryClient = useQueryClient();
  const { data: profile, isLoadingdata: isLoadingProfile, resetData: resetProfile } = useProfile();

  if (isLoadingProfile) {
    return <Loader />;
  }

  const shouldShowPopup = (documentType) => {
    if (!profile) {
      return true;
    }
    return profile.showSharedResourcesPopup !== false;
  };

  const handleCheckboxChange = async (checked) => {
    try {
      if (checked) {
        await neverShowSharedResourcePopup();
        resetProfile();
      }
    } catch (error) {
      console.error('Failed to update popup preference:', error);
    }
  };
  const acceptMutation = useMutation({
    mutationFn: acceptFeedbackRequest,
    onMutate: async (submissionId) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      const previousNotifications = queryClient.getQueryData(['notifications']);
      const updatedNotifications = previousNotifications?.map((n) => {
        if (n.submissionId === submissionId) {
          return { ...n, status: 'ACCEPTED' };
        }
        return n;
      });
      queryClient.setQueryData(
        ['notifications'],
        (old) => updatedNotifications,
      );

      return { previousNotifications };
    },

    onError: (err, newTodo, context) => {
      toast(<Toast message={'' + err.message} />);

      queryClient.setQueryData(
        ['notifications'],
        context.previousNotifications,
      );
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['assignments'] });
      queryClient.refetchQueries({ queryKey: ['tasks'] });
      queryClient.refetchQueries({ queryKey: ['document-reviews'] });
    },
    onSuccess: (data, variables) => {
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['assignments'] });
      queryClient.refetchQueries({ queryKey: ['tasks'] });
      queryClient.refetchQueries({ queryKey: ['document-reviews'] });
      window.location.href = `#/feedbackRequest/documentsReview/${data.id}`;
    },
    onSettled: () => {},
  });

  const declineMutation = useMutation({
    mutationFn: declineFeedbackRequest,
    onMutate: async (submissionId) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      const previousNotifications = queryClient.getQueryData(['notifications']);
      const updatedNotifications = previousNotifications.filter(
        (n) => n.submissionId !== submissionId,
      );
      queryClient.setQueryData(
        ['notifications'],
        (old) => updatedNotifications,
      );

      return { previousNotifications };
    },

    onError: (err, newTodo, context) => {
      queryClient.setQueryData(
        ['notifications'],
        context.previousNotifications,
      );
      queryClient.refetchQueries({ queryKey: ['notifications'] });
    },

    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ['notifications'] });
    },
  });

  const acceptSharedDocumentMutation = useMutation({
    mutationFn: async ({ id, document }) => {
      return await acceptShare(id);
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      const previousNotifications = queryClient.getQueryData(['notifications']);
      const updatedNotifications = previousNotifications?.map((n) => {
        if (n.sharedResource?.id === id || n.id === id) {
          return { ...n, status: 'ACCEPTED' };
        }
        return n;
      });
      queryClient.setQueryData(
        ['notifications'],
        (old) => updatedNotifications,
      );
      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      toast(<Toast message={err.message || 'Failed to accept shared document'} />);
      queryClient.setQueryData(
        ['notifications'],
        context.previousNotifications,
      );
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
    },
    onSuccess: async (data, variables) => {
      const documentType = variables.document?.documentType;
      
      if (documentType === 'COMMENT_BANK') {
        queryClient.invalidateQueries({ queryKey: ['feedbackBanks'] });
        queryClient.invalidateQueries({ queryKey: ['commentBanksWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'commentBanks'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'] });
        
      } else if (documentType === 'MARKING_TEMPLATE') {
        queryClient.invalidateQueries({ queryKey: ['markingCriterias'] });
        queryClient.invalidateQueries({ queryKey: ['markingCriteriasWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'markingCriteria'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'markingCriteria'] });

      } else if (documentType === 'TASK') {
        queryClient.invalidateQueries({ queryKey: ['completedTasks'] });
        queryClient.invalidateQueries({ queryKey: ['assignments'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      } else if (documentType === 'SUCCESS_CRITERIA') {
        queryClient.invalidateQueries({ queryKey: ['successCriteria'] });
        queryClient.invalidateQueries({ queryKey: ['successCriteriaWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'successCriteria'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'] });
        queryClient.refetchQueries({ queryKey: ['successCriteria'] });
        queryClient.refetchQueries({ queryKey: ['successCriteriaWithPreferences'] });
        queryClient.refetchQueries({ queryKey: ['feedbackTool', 'items', 'successCriteria'] });
        queryClient.refetchQueries({ queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'] });
      } else if (documentType === 'SHORT_ANSWERS') {
        queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
        queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'shortAnswers'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'] });
        queryClient.refetchQueries({ queryKey: ['shortAnswers'] });
        queryClient.refetchQueries({ queryKey: ['shortAnswersWithPreferences'] });
        queryClient.refetchQueries({ queryKey: ['feedbackTool', 'items', 'shortAnswers'] });
        queryClient.refetchQueries({ queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'] });
      }

      if (variables.document) {
        setSelectedDocument(variables.document);
        const documentType = variables.document?.documentType;
        if (shouldShowPopup(documentType)) {
          setShowSuccessPopup(true);
        }
      }
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
    },
  });

  const rejectSharedDocumentMutation = useMutation({
    mutationFn: ({ id }) => rejectShare(id),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      const previousNotifications = queryClient.getQueryData(['notifications']);
      const updatedNotifications = previousNotifications?.map((n) => {
        if (n.sharedResource?.id === id || n.id === id) {
          return { ...n, status: 'REJECTED' };
        }
        return n;
      });
      queryClient.setQueryData(
        ['notifications'],
        (old) => updatedNotifications,
      );
      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      toast(<Toast message={err.message || 'Failed to reject shared document'} />);
      queryClient.setQueryData(
        ['notifications'],
        context.previousNotifications,
      );
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
      setShowRejectPopup(false);
      setSelectedDocument(null);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
      setShowRejectPopup(false);
      setSelectedDocument(null);
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      queryClient.refetchQueries({ queryKey: ['shares'] });
    },
  });

  const handleRejectClick = (id, document) => {
    setSelectedDocument(document);
    setShowRejectPopup(true);
  };

  const handleConfirmReject = () => {
    if (selectedDocument?.id) {
      rejectSharedDocumentMutation.mutate({ id: selectedDocument.id });
    }
  };

  const handlePreviewClick = async (notification) => {
    const itemId = notification.sharedResource?.item?.id || notification.sharedResource?.itemId || notification.itemId;
    const itemType = notification.sharedResource?.itemType || notification.itemType;
    
    if (!itemId) return;
    
    setPreviewedNotification(notification);
    setIsLoadingPreview(true);
    try {
      const isCommentBank = itemType === 'COMMENT_BANK' || itemType === 'COMMENT_BANKS';
      const isTask = itemType === 'TASK';
      const isSuccessCriteria = itemType === 'SUCCESS_CRITERIA';
      const isShortAnswer = itemType === 'SHORT_ANSWERS';

      if (isCommentBank) {
        const data = await getCommentBank(itemId);
        setPreviewData(data || { smartComments: [] });
      } else if (isTask) {
        const data = await getAssignmentById(itemId);
        setPreviewData(data || { title: 'Untitled Task' });
      } else if (isSuccessCriteria) {
        const response = await getSuccessCriteria(itemId);
        const rawData = Array.isArray(response) && response.length > 0 ? response[0] : response;
        const data = rawData || { title: 'Untitled Success Criteria', criterias: [] };
        if (!data.criterias) {
          data.criterias = [];
        }
        setPreviewData(data);
      } else if (isShortAnswer) {
        const response = await getShortAnswer(itemId);
        const rawData = Array.isArray(response) && response.length > 0 ? response[0] : response;
        const data = rawData || { title: 'Untitled Short Answer', highestMarks: 0, criterias: [] };
        if (!data.criterias) {
          data.criterias = [];
        }
        setPreviewData(data);
      } else {
        let data = await getMarkingMethodology(itemId);
        if (Array.isArray(data) && data.length > 0) {
          data = data[0];
        }
        setPreviewData(data || { type: 'RUBRICS', criterias: [] });
      }
      setShowPreviewDialog(true);
    } catch (error) {
      const isCommentBank = itemType === 'COMMENT_BANK' || itemType === 'COMMENT_BANKS';
      const isTask = itemType === 'TASK';
      const isSuccessCriteria = itemType === 'SUCCESS_CRITERIA';
      const isShortAnswer = itemType === 'SHORT_ANSWERS';
      const emptyData = isCommentBank
        ? { smartComments: [] }
        : isTask
        ? { title: 'Untitled Task' }
        : isSuccessCriteria
        ? { title: 'Untitled Success Criterias', criterias: [] }
        : isShortAnswer
        ? { title: 'Untitled Short Answer', highestMarks: 0, criterias: [] }
        : { type: 'RUBRICS', criterias: [] };
      setPreviewData(emptyData);
      setShowPreviewDialog(true);
    } finally {
      setIsLoadingPreview(false);
    }
  };


  const { notifications, type, onCloseFn, loadingNotifications } = props;
  if (!notifications || notifications?.length === 0) {
    return (
      <>
        {type == 'small' ? (
          <NavbarDiv>
            <Frame1409>
              <Frame15>
                {loadingNotifications ? (
                  <SmallLoader />
                ) : (
                  <Cards emptyCard={true} />
                )}
              </Frame15>
              <MaskGroup src="/img/close.png" onClick={onCloseFn} />
            </Frame1409>
          </NavbarDiv>
        ) : (
          <Frame16>
            {loadingNotifications ? (
              <SmallLoader />
            ) : (
              <Frame15>
                <NotificationHead>
                  <HeaderLeft>
                    <HeadingImage src={notificationbing} />
                    <Heading>Notifications</Heading>
                  </HeaderLeft>
                  <CloseImage src="img/closecircle.svg" onClick={onCloseFn} />
                </NotificationHead>
                <Cards emptyCard={true} />
              </Frame15>
            )}
          </Frame16>
        )}
      </>
    );
  }

  const isSharedDocumentNotification = (notification) => {
    const itemType = notification?.sharedResource?.itemType;
    return (
      notification?.sharedResource &&
      (itemType === 'MARKING_TEMPLATE' || itemType === 'COMMENT_BANK' || itemType === 'TASK' || itemType === 'SUCCESS_CRITERIA' || itemType === 'SHORT_ANSWERS')
    );
  };

  const getSharedItemLabel = (itemType, count) => {
    if (itemType === 'COMMENT_BANK') {
      return count === 1 ? 'comment bank' : 'comment banks';
    }
    if (itemType === 'TASK') {
      return count === 1 ? 'task' : 'tasks';
    }
    if (itemType === 'SUCCESS_CRITERIA') {
      return count === 1 ? 'success criteria' : 'success criteria';
    }
    if (itemType === 'SHORT_ANSWERS') {
      return count === 1 ? 'short answer' : 'short answers';
    }
    return count === 1 ? 'rubric' : 'rubrics';
  };

  const sharedNotificationsMap = {};
  const sharedNotificationsOrder = [];
  const notificationEntries = [];

  notifications.forEach((notification, index) => {
    if (isSharedDocumentNotification(notification)) {
      const sharedBy = notification.sharedResource.sharedBy;
      const itemType = notification.sharedResource.itemType;
      const key = `${notification.sharedResource.sharedBy}-${itemType}`;

      if (!sharedNotificationsMap[key]) {
        sharedNotificationsMap[key] = {
          order: index,
          sharedBy,
          itemType,
          items: [],
        };
        sharedNotificationsOrder.push(key);
      }

      sharedNotificationsMap[key].items.push(notification);
      return;
    }

    if (notification.type === 'FEEDBACK_REQUEST') {
      notificationEntries.push({
        order: index,
        node: (
          <TaskCard
            key={notification.submissionId || index}
            task={notification}
            small={true}
            showThreeDots={false}
            notification={true}
            onAccept={() => acceptMutation.mutate(notification.submissionId)}
            onDecline={() => declineMutation.mutate(notification.submissionId)}
          />
        ),
      });
      return;
    }

    notificationEntries.push({
      order: index,
      node: (
        <TaskCard
          key={notification.id || index}
          task={notification}
          small={true}
          showThreeDots={false}
          notification={true}
        />
      ),
    });
  });

  const sharedNotificationEntries = sharedNotificationsOrder.map((key) => {
    const group = sharedNotificationsMap[key];
    const count = group.items.length;

    return {
      order: group.order,
      node: (
        <SharedNotificationContainer key={`shared-${key}`}>
          <SharedNotificationTitle>
            <SharedNotificationSender>{group.sharedBy}</SharedNotificationSender>{' '}
            has shared {count} {getSharedItemLabel(group.itemType, count)} with you
          </SharedNotificationTitle>

          <SharedNotificationList>
            {group.items.map((item, itemIndex) => {
              const documentTitle =
                item.sharedResource?.item?.title || 'Untitled';
              const sharedDocumentId = item.sharedResource?.id || item.id;

              return (
                <SharedNotificationItem
                  key={sharedDocumentId || itemIndex}
                >
                  <SharedNotificationItemTitle>
                    {documentTitle}
                  </SharedNotificationItemTitle>
                    <ActionButtons>
                      <PreviewButton
                        onClick={() => handlePreviewClick(item)}
                        disabled={isLoadingPreview || acceptSharedDocumentMutation.isPending || rejectSharedDocumentMutation.isPending}
                      >
                        <img src={previewIcon} alt="Preview" /> Preview
                      </PreviewButton>
                      <AcceptButton
                        onClick={() => {
                          const itemType = item.sharedResource?.itemType || group.itemType || item.itemType;
                          acceptSharedDocumentMutation.mutate({
                            id: sharedDocumentId,
                            document: {
                              id: sharedDocumentId,
                              itemTitle: documentTitle,
                              documentType: itemType,
                            },
                          });
                        }}
                        disabled={acceptSharedDocumentMutation.isPending || rejectSharedDocumentMutation.isPending}
                      >
                        <img src={acceptIcon} alt="Accept" /> Accept
                      </AcceptButton>
                      <RejectButton
                        onClick={() => {
                          handleRejectClick(sharedDocumentId, {
                            id: sharedDocumentId,
                            itemTitle: documentTitle,
                            documentType: item.sharedResource?.itemType || group.itemType,
                          });
                        }}
                        disabled={acceptSharedDocumentMutation.isPending || rejectSharedDocumentMutation.isPending}
                      >
                        <img src={rejectIcon} alt="Reject" /> Reject
                      </RejectButton>
                    </ActionButtons>
                </SharedNotificationItem>
              );
            })}
          </SharedNotificationList>
        </SharedNotificationContainer>
      ),
    };
  });

  const notificationFrames = [...notificationEntries, ...sharedNotificationEntries]
    .sort((a, b) => a.order - b.order)
    .map((entry) => entry.node);

  return (
    <>
      {type == 'small' ? (
        <NavbarDiv>
          <Frame1409>
            <NotificationHead>
              <HeadingImage src={notificationbing} />
              <Heading>Notifications</Heading>
              <CloseNotification src="/img/close.png" onClick={onCloseFn} />
            </NotificationHead>
            <Frame16>
              {notificationFrames.length > 0 ? (
                notificationFrames
              ) : (
                <EmptyBox>No new notifications</EmptyBox>
              )}
            </Frame16>
          </Frame1409>
        </NavbarDiv>
      ) : (
        <Frame15>
          <NotificationHead>
            <HeaderLeft>
              <HeadingImage src={notificationbing} />
              <Heading>Notifications</Heading>
            </HeaderLeft>
            <CloseImage src="img/closecircle.svg" onClick={onCloseFn} />
          </NotificationHead>
          <NotificationBody>
            {notificationFrames.length > 0 ? (
              notificationFrames
            ) : (
              <EmptyBox>No new notifications</EmptyBox>
            )}
          </NotificationBody>
        </Frame15>
      )}
      <ActionPopup
        open={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
          setSelectedDocument(null);
        }}
        type="success"
        message={`The shared ${selectedDocument?.documentType === 'MARKING_TEMPLATE' ? 'rubric' : selectedDocument?.documentType === 'TASK' ? 'task' : selectedDocument?.documentType === 'SUCCESS_CRITERIA' ? 'success criteria' : selectedDocument?.documentType === 'SHORT_ANSWERS' ? 'short answer' : 'comment bank'} has been added to your ${selectedDocument?.documentType === 'MARKING_TEMPLATE' ? 'Rubrics' : selectedDocument?.documentType === 'TASK' ? 'Tasks' : selectedDocument?.documentType === 'SUCCESS_CRITERIA' ? 'Success Criterias' : selectedDocument?.documentType === 'SHORT_ANSWERS' ? 'Short Answers' : 'Comment Banks'}.`}
        primaryButtonLabel={`Go to ${selectedDocument?.documentType === 'MARKING_TEMPLATE' ? 'Rubrics' : selectedDocument?.documentType === 'TASK' ? 'Tasks' : selectedDocument?.documentType === 'SUCCESS_CRITERIA' ? 'Success Criterias' : selectedDocument?.documentType === 'SHORT_ANSWERS' ? 'Short Answers' : 'Comment Banks'}`}
        primaryButtonAction={() => {
          setShowSuccessPopup(false);
          setSelectedDocument(null);
          if (onCloseFn) {
            onCloseFn();
          }
          if (window.closeNotificationBar) {
            window.closeNotificationBar();
          }
          const path = selectedDocument?.documentType === 'MARKING_TEMPLATE' ? '/#/tools/markingcriterias' : selectedDocument?.documentType === 'TASK' ? '/#/tasks' : selectedDocument?.documentType === 'SUCCESS_CRITERIA' ? '/#/tools/successcriterias' : selectedDocument?.documentType === 'SHORT_ANSWERS' ? '/#/tools/shortanswers' : '/#/tools/commentbanks';
          window.location.href = path;
        }}
        showCheckbox={true}
        onCheckboxChange={handleCheckboxChange}
      />
      <ActionPopup
        open={showRejectPopup}
        onClose={() => {
          setShowRejectPopup(false);
          setSelectedDocument(null);
        }}
        type="reject"
        title={`Reject shared ${selectedDocument?.documentType === 'TASK' ? 'task' : selectedDocument?.documentType === 'SUCCESS_CRITERIA' ? 'success criteria' : selectedDocument?.documentType === 'SHORT_ANSWERS' ? 'short answer' : 'template'}?`}
        message={`Once rejected the shared ${selectedDocument?.documentType === 'TASK' ? 'task' : selectedDocument?.documentType === 'SUCCESS_CRITERIA' ? 'success criteria' : selectedDocument?.documentType === 'SHORT_ANSWERS' ? 'short answer' : 'template'} will be lost forever. Are you sure you want to continue?`}
        primaryButtonLabel="Reject"
        primaryButtonAction={handleConfirmReject}
        secondaryButtonLabel="Keep"
        secondaryButtonAction={() => {
          setShowRejectPopup(false);
          setSelectedDocument(null);
        }}
        showDottedBorder={true}
      />
      {showPreviewDialog && previewData && (
        <>
          {previewData.smartComments !== undefined ? (
            <CommentBankDialog
              setCommentBankPreviewDialog={(value) => {
                setShowPreviewDialog(value);
                if (!value) setPreviewData(null);
              }}
              commentBank={previewData}
              showEditButton={false}
            />
          ) : previewData.questions !== undefined || (previewData.title && previewData.classIds !== undefined) ? (
            <TaskPreviewDialog
              setTaskPreviewDialog={(value) => {
                setShowPreviewDialog(value);
                if (!value) {
                  setPreviewData(null);
                  setPreviewedNotification(null);
                }
              }}
              task={previewData}
              shareId={previewedNotification?.sharedResource?.id || previewedNotification?.id}
              resourceType={previewedNotification?.sharedResource?.itemType || previewedNotification?.itemType}
              useNotificationsQuery={true}
              onAcceptSuccess={(responseData, resourceInfo) => {
                const documentType = resourceInfo.resourceType;

                if (documentType === 'COMMENT_BANK') {
                  queryClient.invalidateQueries({ queryKey: ['feedbackBanks'] });
                  queryClient.invalidateQueries({ queryKey: ['commentBanksWithPreferences'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'commentBanks'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'] });
                } else if (documentType === 'MARKING_TEMPLATE') {
                  queryClient.invalidateQueries({ queryKey: ['markingCriterias'] });
                  queryClient.invalidateQueries({ queryKey: ['markingCriteriasWithPreferences'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'markingCriteria'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'markingCriteria'] });
                } else if (documentType === 'TASK') {
                  queryClient.invalidateQueries({ queryKey: ['completedTasks'] });
                  queryClient.invalidateQueries({ queryKey: ['assignments'] });
                  queryClient.invalidateQueries({ queryKey: ['tasks'] });
                } else if (documentType === 'SUCCESS_CRITERIA') {
                  queryClient.invalidateQueries({ queryKey: ['successCriteria'] });
                  queryClient.invalidateQueries({ queryKey: ['successCriteriaWithPreferences'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'successCriteria'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'] });
                  queryClient.refetchQueries({ queryKey: ['successCriteria'] });
                  queryClient.refetchQueries({ queryKey: ['successCriteriaWithPreferences'] });
                  queryClient.refetchQueries({ queryKey: ['feedbackTool', 'items', 'successCriteria'] });
                  queryClient.refetchQueries({ queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'] });
                } else if (documentType === 'SHORT_ANSWERS') {
                  queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
                  queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'shortAnswers'] });
                  queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'] });
                  queryClient.refetchQueries({ queryKey: ['shortAnswers'] });
                  queryClient.refetchQueries({ queryKey: ['shortAnswersWithPreferences'] });
                  queryClient.refetchQueries({ queryKey: ['feedbackTool', 'items', 'shortAnswers'] });
                  queryClient.refetchQueries({ queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'] });
                }

                setSelectedDocument({
                  id: resourceInfo.id,
                  documentType: documentType,
                  itemTitle: previewedNotification?.sharedResource?.item?.title || 'Untitled',
                });

                if (shouldShowPopup(documentType)) {
                  setShowSuccessPopup(true);
                } else {
                  if (onCloseFn) {
                    onCloseFn();
                  }
                  if (window.closeNotificationBar) {
                    window.closeNotificationBar();
                  }
                  const path = documentType === 'MARKING_TEMPLATE' ? '/#/tools/markingcriterias' : documentType === 'TASK' ? '/#/tasks' : documentType === 'SUCCESS_CRITERIA' ? '/#/tools/successcriterias' : documentType === 'SHORT_ANSWERS' ? '/#/tools/shortanswers' : '/#/tools/commentbanks';
                  window.location.href = path;
                }
              }}
              onClose={() => {
                setShowPreviewDialog(false);
                setPreviewData(null);
                setPreviewedNotification(null);
              }}
            />
          ) : (previewedNotification?.sharedResource?.itemType === 'SHORT_ANSWERS' || previewedNotification?.itemType === 'SHORT_ANSWERS' || (previewData.highestMarks !== undefined && previewData.criterias)) ? (
            <ShortAnswerDialog
              shortAnswer={previewData}
              setShortAnswerPreviewDialog={(value) => {
                setShowPreviewDialog(value);
                if (!value) {
                  setPreviewData(null);
                  setPreviewedNotification(null);
                }
              }}
              showActionButton={false}
            />
          ) : (previewedNotification?.sharedResource?.itemType === 'SUCCESS_CRITERIA' || previewedNotification?.itemType === 'SUCCESS_CRITERIA' || (previewData.criterias && !previewData.type && !previewData.highestMarks)) ? (
            <SuccessCriteriaDialog
              successCriteria={previewData}
              setSuccessCriteriaPreviewDialog={(value) => {
                setShowPreviewDialog(value);
                if (!value) {
                  setPreviewData(null);
                  setPreviewedNotification(null);
                }
              }}
              showActionButton={false}
              showEditButton={false}
            />
          ) : (
            <PreviewDialog
              setMarkingCriteriaPreviewDialog={(value) => {
                setShowPreviewDialog(value);
                if (!value) setPreviewData(null);
              }}
              markingCriterias={previewData}
              showEditButton={false}
            />
          )}
        </>
      )}
    </>
  );
}

export default NotificationsBar;